import { injectable, inject } from 'inversify'
import { Config } from '../config'
import { UserModel } from '@core/infra/authentication/user.model'
import type { GatewayResponse, NotFoundDetail, ServerResponse, ValidationIssue } from './api-response.types'
import { Types } from '@core/ioc/types'
import type { LoggingGateway } from './logging.gateway'

type HttpMethod = 'GET' | 'POST' | 'DELETE'

// Authentication endpoints are excluded from the 401-triggers-refresh flow:
// /refresh would recurse, while login/register/guest do not represent work
// owned by an already-authenticated session.
// /share-link/session is here for the same reason as /guest: it establishes a
// session rather than doing work for one that already exists. A stale token in
// UserModel must not let a 401 from an invalid link get "fixed" by a refresh
// that then masks the link failure behind the old identity.
const NO_REFRESH_PATHS = ['/refresh', '/login', '/register', '/guest', '/share-link/session']

/**
 * Required on every call, not optional: an omitted/`undefined` option is a
 * compile error rather than a silent default (async-operation-system-
 * specification.md §13, §5.1's null-means-deliberately-empty discipline
 * applied to a parameter, not just a field). `signal` carries an Operation
 * execution's cancellation; `idempotencyKey` is sent as `Idempotency-Key`
 * only when non-null — every read passes `null` (a GET carries no body to
 * replay), and a write passes one only when its own operation definition is
 * `safety: { kind: 'idempotency-keyed-write' }` (spec §21 Phase 2 item 3;
 * `server/duke-api.ts`'s `withIdempotency` is what honors it).
 */
export interface HttpRequestOptions {
  readonly signal: AbortSignal | null
  readonly idempotencyKey: string | null
}

/**
 * Wraps `fetch` (invariant 8: the sole I/O boundary for the backend API).
 * The auth token is read from `UserModel` on every call rather than cached
 * on this class, so a refreshed token is picked up on the very next request
 * with nothing to invalidate here. See `:40-47` below for the 401-triggers-
 * refresh split: this Gateway only decides *when* to ask for a refresh and
 * retry once — what refresh does stays `AuthenticationRepository`'s own
 * Operation.
 */
@injectable()
export class HttpGateway {
  @inject(Config)
  private readonly config!: Config

  @inject(UserModel)
  private readonly userModel!: UserModel

  @inject(Types.ILoggingGateway)
  private readonly loggingGateway!: LoggingGateway

  // Set by AuthenticationRepository (which already depends on this gateway) —
  // a plain callback instead of injecting AuthenticationRepository here, which
  // would be a circular DI edge.
  // Set by AuthenticationRepository's own `authentication.refresh` Operation
  // handle, which now owns deduping concurrent 401s onto one in-flight
  // refresh — this Gateway just requests one refresh/retry per 401 and
  // honors whatever that handle decides; it does not own concurrency state
  // itself (async-operation-system-specification.md §21 Phase 1 step 11).
  refreshHandler: (() => Promise<boolean>) | null = null

  // No Content-Type for FormData: the browser sets `multipart/form-data` with
  // its own boundary, and setting it by hand strips that boundary and the
  // server can no longer parse the body.
  private headers(idempotencyKey: string | null, isFormData: boolean): Record<string, string> {
    const headers: Record<string, string> = isFormData ? {} : { 'Content-Type': 'application/json' }
    if (this.userModel.token) {
      headers.Authorization = `Bearer ${this.userModel.token}`
    }
    if (idempotencyKey !== null) {
      headers['Idempotency-Key'] = idempotencyKey
    }
    return headers
  }

  async get(path: string, options: HttpRequestOptions): Promise<GatewayResponse> {
    return this.request('GET', path, options)
  }

  async post(path: string, requestDto: unknown, options: HttpRequestOptions): Promise<GatewayResponse> {
    return this.request('POST', path, options, requestDto)
  }

  /** Same envelope and 401-retry handling as `post`, just a multipart body instead of JSON — for file uploads. */
  async postFile(path: string, formData: FormData, options: HttpRequestOptions): Promise<GatewayResponse> {
    return this.request('POST', path, options, formData)
  }

  async delete(path: string, options: HttpRequestOptions): Promise<GatewayResponse> {
    return this.request('DELETE', path, options)
  }

  private async request(
    method: HttpMethod,
    path: string,
    options: HttpRequestOptions,
    body?: unknown,
    isRetry = false,
  ): Promise<GatewayResponse> {
    const label = `HTTP ${method} ${path}`
    const isFormData = body instanceof FormData

    try {
      const response = await fetch(this.config.apiUrl + path, {
        method,
        headers: this.headers(options.idempotencyKey, isFormData),
        ...(options.signal !== null ? { signal: options.signal } : {}),
        ...(body !== undefined ? { body: isFormData ? body : JSON.stringify(body) } : {}),
      })

      if (response.status === 401 && !isRetry && !NO_REFRESH_PATHS.includes(path) && this.refreshHandler) {
        const refreshed = await this.refreshHandler()
        if (refreshed) {
          return this.request(method, path, options, body, true)
        }
      }

      const raw = await response.text()

      // Parse before checking response.ok: a non-2xx still carries the API's own
      // envelope, and that message is the only one written for a human
      // ("Invalid email or password" on a 401). Replacing it with our own
      // status-line text would throw away the sole useful explanation.
      const dto = this.parseServerResponse(raw)
      if (dto?.success && response.ok) return dto
      if (dto && !dto.success) {
        return {
          success: false,
          failure: {
            kind: 'rejected',
            status: response.status,
            message: dto.result.message,
            issues: dto.result.issues,
            notFound: dto.result.notFound,
          },
        }
      }

      // Nothing usable came back. Details stay in logging; the Black Box gets
      // only a typed mechanism outcome that a Repository can translate.
      if (!response.ok) {
        this.loggingGateway.error(`${label} failed`, {
          status: response.status,
          statusText: response.statusText,
        })
        return {
          success: false,
          failure: { kind: 'transport', reason: 'http', status: response.status },
        }
      }
      this.loggingGateway.error(`${label} returned an invalid response`, { raw })
      return { success: false, failure: { kind: 'transport', reason: 'invalid-response' } }
    } catch (err) {
      this.loggingGateway.error(`${label} failed`, { err })
      return { success: false, failure: { kind: 'transport', reason: 'network' } }
    }
  }

  /**
   * A response body is only usable if it is the API's `{ success, result }`
   * envelope. Anything else — empty body, HTML error page, malformed JSON —
   * returns null so the caller falls back to describing the transport failure.
   */
  private parseServerResponse(raw: string): ServerResponse | null {
    if (!raw.trim()) return null

    try {
      const dto: unknown = JSON.parse(raw)
      if (dto === null || typeof dto !== 'object' || !('success' in dto) || !('result' in dto)) return null

      const candidate = dto as { success: unknown; result: unknown }
      if (candidate.success === true) return { success: true, result: candidate.result }

      if (
        candidate.success === false &&
        candidate.result !== null &&
        typeof candidate.result === 'object' &&
        'message' in candidate.result &&
        typeof candidate.result.message === 'string'
      ) {
        return {
          success: false,
          result: {
            message: candidate.result.message,
            issues: this.parseIssues(candidate.result),
            notFound: this.parseNotFound(candidate.result),
          },
        }
      }

      return null
    } catch {
      return null
    }
  }

  // Untrusted external data (same discipline as parseServerResponse above):
  // present only for a 422 validation failure, so a missing/malformed
  // `issues` array is not an error — it just means there are none.
  private parseIssues(result: object): readonly ValidationIssue[] | undefined {
    if (!('issues' in result) || !Array.isArray(result.issues)) return undefined

    const issues = result.issues.filter(
      (issue): issue is ValidationIssue =>
        issue !== null &&
        typeof issue === 'object' &&
        typeof issue.path === 'string' &&
        typeof issue.message === 'string',
    )
    return issues.length > 0 ? issues : undefined
  }

  // Untrusted external data, same discipline: present only for a 404.
  private parseNotFound(result: object): NotFoundDetail | undefined {
    if (!('notFound' in result) || result.notFound === null || typeof result.notFound !== 'object') return undefined

    const candidate = result.notFound as { resource?: unknown; id?: unknown }
    if (typeof candidate.resource !== 'string' || typeof candidate.id !== 'string') return undefined

    return { resource: candidate.resource, id: candidate.id }
  }
}
